import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { opportunityApi, userApi, watchListApi, pipelineApi, savedViewsApi, PscOption, SavedView as SavedViewRow, SavedViewBody } from '../../services/api';
import { Opportunity } from '../../types';
import {
  MagnifyingGlassIcon,
  StarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  FunnelIcon,
  DocumentTextIcon,
  SparklesIcon,
  MapPinIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from 'components/icons';
import { StarIcon as StarIconSolid } from 'components/icons/solid';
import { useAuth } from '../../hooks/useAuth';
import NaicsCodePicker from '../../components/ui/NaicsCodePicker';
import { formatAgency } from '../../utils/agency';
import { cardAgency, sourceInfo } from '../../utils/source';
import { formatLocation } from '../../utils/location';
import { SET_ASIDE_TYPES, US_STATES, CheckList, MultiLabel, AgencyTree, AgencyOption, agencyLabel } from '../../components/filters/pickers';
import Modal from '../../components/ui/Modal';
import PageHeader from '../../components/ui/PageHeader';
import EmptyState from '../../components/ui/EmptyState';
import ErrorState from '../../components/ui/ErrorState';
import { SkeletonRows } from '../../components/ui/Skeleton';
import PreferenceConfirmation from '../../components/prompts/PreferenceConfirmation';
import MatchesPanel from '../../components/opportunities/MatchesPanel';
import LearningFeedback from '../../components/prompts/LearningFeedback';

const PROCUREMENT_TYPES = [
  { code: 'Solicitation', label: 'Solicitation' },
  { code: 'Presolicitation', label: 'Pre-solicitation' },
  { code: 'Combined Synopsis/Solicitation', label: 'Combined Synopsis/Solicitation' },
  { code: 'Award Notice', label: 'Award Notice' },
  { code: 'Sources Sought', label: 'Sources Sought' },
  { code: 'Special Notice', label: 'Special Notice' },
  { code: 'Justification', label: 'Justification (J&A)' },
  { code: 'Intent to Bundle', label: 'Intent to Bundle' },
  { code: 'Sale of Surplus Property', label: 'Sale of Surplus Property' },
];

// Place-of-performance states: the 50 states, DC, and the territories SAM.gov uses.

interface Filters {
  /** Notice types to include. Empty means every type. */
  ptype: string[];
  /** Set-aside codes; any of them matches. Empty means any. */
  setAside: string[];
  /** SAM department names; any of them matches. Empty means any agency. */
  agency: string[];
  /** NAICS codes; any of them matches. Empty means any. */
  naicsCode: string[];
  /** Product Service Codes (what is being bought); a code or a prefix, any matches. */
  psc: string[];
  /** Off by default — the list is for work you can still bid on. */
  includeExpired: boolean;
  /** Place-of-performance state codes. Empty means anywhere. */
  state: string[];
  /**
   * With a state chosen, also show opportunities that name no state — about
   * two-thirds of them. On by default; they're listed after the state's own.
   */
  includeUnspecifiedState: boolean;
}

const DEFAULT_FILTERS: Filters = {
  ptype: ['Solicitation'], setAside: [], agency: [], naicsCode: [], psc: [], includeExpired: false,
  state: [], includeUnspecifiedState: true,
};

type SortKey = 'posted:desc' | 'posted:asc' | 'due:asc' | 'due:desc';
const DEFAULT_SORT: SortKey = 'posted:desc';

// Filters, search text, sort and page size survive leaving the page and
// coming back, and a reload. Stored per browser; a filter set once should not
// have to be set again every visit. Clear All puts everything back.
const STORAGE_KEY = 'clevor.opportunities.view.v1';
interface SavedView { filters: Filters; searchQuery: string; sort: SortKey; limit: number; activeViewId?: string | null }

// Named views: the sticky blob with a name and a shelf. Colors are Tailwind
// classes spelled out so the build keeps them.
const VIEW_COLORS: Array<{ key: string; dot: string }> = [
  { key: 'slate', dot: 'bg-slate-500' }, { key: 'blue', dot: 'bg-blue-500' }, { key: 'purple', dot: 'bg-purple-500' }, { key: 'amber', dot: 'bg-amber-500' },
  { key: 'green', dot: 'bg-green-500' }, { key: 'teal', dot: 'bg-teal-500' }, { key: 'indigo', dot: 'bg-indigo-500' }, { key: 'rose', dot: 'bg-rose-500' },
];
const dotClass = (color: string) => VIEW_COLORS.find((c) => c.key === color)?.dot || 'bg-slate-500';
const sortedEq = (a: string[], b: string[]) => a.length === b.length && [...a].sort().every((x, i) => x === [...b].sort()[i]);
function sameBody(a: SavedViewBody, b: SavedViewBody): boolean {
  const f = a.filters, g = b.filters;
  return sortedEq(f.ptype, g.ptype) && sortedEq(f.setAside, g.setAside) && sortedEq(f.agency, g.agency) && sortedEq(f.naicsCode, g.naicsCode)
    && sortedEq(f.psc, g.psc) && sortedEq(f.state, g.state) && f.includeExpired === g.includeExpired && f.includeUnspecifiedState === g.includeUnspecifiedState
    && a.searchQuery.trim() === b.searchQuery.trim() && a.sort === b.sort;
}
function bodyToFilters(v: SavedViewBody): Filters {
  return {
    ptype: v.filters.ptype.length ? v.filters.ptype : DEFAULT_FILTERS.ptype, setAside: v.filters.setAside, agency: v.filters.agency,
    naicsCode: v.filters.naicsCode, psc: v.filters.psc, state: v.filters.state,
    includeExpired: v.filters.includeExpired === true, includeUnspecifiedState: v.filters.includeUnspecifiedState !== false,
  };
}
const asStringList = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
function loadSavedView(): SavedView {
  const fallback: SavedView = { filters: DEFAULT_FILTERS, searchQuery: '', sort: DEFAULT_SORT, limit: 25 };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    const f = v?.filters ?? {};
    return {
      filters: {
        ptype: asStringList(f.ptype).length ? asStringList(f.ptype) : DEFAULT_FILTERS.ptype,
        setAside: asStringList(f.setAside),
        agency: asStringList(f.agency),
        naicsCode: asStringList(f.naicsCode),
        psc: asStringList(f.psc),
        state: asStringList(f.state),
        includeExpired: f.includeExpired === true,
        includeUnspecifiedState: f.includeUnspecifiedState !== false,
      },
      searchQuery: typeof v?.searchQuery === 'string' ? v.searchQuery : '',
      sort: (['posted:desc', 'posted:asc', 'due:asc', 'due:desc'] as SortKey[]).includes(v?.sort) ? v.sort : DEFAULT_SORT,
      limit: [25, 50, 100, 250].includes(v?.limit) ? v.limit : 25,
      activeViewId: typeof v?.activeViewId === 'string' ? v.activeViewId : null,
    };
  } catch {
    return fallback;
  }
}
function saveView(view: SavedView) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(view)); } catch { /* storage unavailable; nothing to do */ }
}

/**
 * A column header that sorts. Shows a faint up/down glyph until active, then
 * the arrow for the current direction; exposes aria-sort for screen readers.
 */
function SortableHeader({ label, column, sort, onSort, className }: {
  label: string;
  column: 'posted' | 'due';
  sort: SortKey;
  onSort: (next: SortKey) => void;
  className?: string;
}) {
  const [activeCol, dir] = sort.split(':') as ['posted' | 'due', 'asc' | 'desc'];
  const active = activeCol === column;
  // First click on a column picks the order people want from it: newest
  // postings, soonest deadlines. Clicking again flips it.
  const next: SortKey = active
    ? (`${column}:${dir === 'asc' ? 'desc' : 'asc'}` as SortKey)
    : column === 'posted' ? 'posted:desc' : 'due:asc';
  const Icon = !active ? ChevronUpDownIcon : dir === 'asc' ? ChevronUpIcon : ChevronDownIcon;
  return (
    <th
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      className={`px-4 py-3 text-left text-xs font-medium uppercase ${className ?? ''}`}
    >
      <button
        type="button"
        onClick={() => onSort(next)}
        title={active ? `Sorted ${dir === 'asc' ? 'oldest' : 'newest'} first — click to flip` : `Sort by ${label.toLowerCase()}`}
        className={`group inline-flex items-center gap-1 uppercase hover:text-content ${active ? 'text-content' : 'text-content-muted'}`}
      >
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? '' : 'opacity-40 group-hover:opacity-100'}`} aria-hidden="true" />
      </button>
    </th>
  );
}

const timeAgo = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return 'today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

const timeFromNow = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  if (diffMs < 0) return 'Expired';
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 1) return '< 1d';
  if (diffDays === 1) return '1 day';
  if (diffDays < 7) return `${diffDays} days`;
  return `${Math.floor(diffDays / 7)}w left`;
};

const Opportunities: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  // Read per mount, not per bundle load: navigating away and back must
  // restore the filters as last edited, not as they were when the app opened.
  const savedView = useRef(loadSavedView()).current;
  const [searchQuery, setSearchQuery] = useState(savedView.searchQuery);
  const [, setIsLimited] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [agencyOptions, setAgencyOptions] = useState<AgencyOption[]>([]);
  const [agencyLoadFailed, setAgencyLoadFailed] = useState(false);
  const [agencySearch, setAgencySearch] = useState('');
  const [pscOptions, setPscOptions] = useState<PscOption[]>([]);
  const [pscSearch, setPscSearch] = useState('');
  const [summaries, setSummaries] = useState<Record<string, string>>({});
  const [loadingSummaries, setLoadingSummaries] = useState(false);
  const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set());
  const [togglingWatch, setTogglingWatch] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const handleToggleWatch = async (opportunityId: string) => {
    if (togglingWatch) return;
    const isWatched = watchedIds.has(opportunityId);
    setTogglingWatch(opportunityId);
    try {
      if (isWatched) {
        await watchListApi.remove(opportunityId);
        setWatchedIds(prev => {
          const next = new Set(prev);
          next.delete(opportunityId);
          return next;
        });
        toast.success('Removed from watch list');
      } else {
        await watchListApi.add(opportunityId);
        setWatchedIds(prev => new Set(prev).add(opportunityId));
        toast.success('Added to watch list');
      }
    } catch {
      toast.error(isWatched ? 'Could not remove from watch list' : 'Could not add to watch list');
    } finally {
      setTogglingWatch(null);
    }
  };

  // Instant Value Mode state
  const isNewUser = !user?.naicsCodes || user.naicsCodes.length === 0;
  const [showRefineBanner, setShowRefineBanner] = useState(isNewUser);
  const [showRefineModal, setShowRefineModal] = useState(false);
  const [refineNaics, setRefineNaics] = useState<string[]>([]);
  const [refineSetAside, setRefineSetAside] = useState('');
  const [refineKeywords, setRefineKeywords] = useState('');
  const [refineSubmitting, setRefineSubmitting] = useState(false);
  const [isNearMatch, setIsNearMatch] = useState(false);
  const [inferredPrefs, setInferredPrefs] = useState<any>(null);

  const handleRefineSubmit = async () => {
    setRefineSubmitting(true);
    try {
      await userApi.updateMe({
        naicsCodes: refineNaics,
      });
      await refreshUser();

      // Apply refinements as filters
      setFilters(f => ({
        ...f,
        naicsCode: refineNaics,
        setAside: refineSetAside ? [refineSetAside] : [],
      }));
      if (refineKeywords) setSearchQuery(refineKeywords);

      setShowRefineModal(false);
      setShowRefineBanner(false);
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setRefineSubmitting(false);
    }
  };

  const [filters, setFilters] = useState<Filters>(savedView.filters);
  const [sort, setSort] = useState<SortKey>(savedView.sort);
  const sortTouched = useRef(false);
  // Set when a filter chip is removed, so the next filters change refetches.
  // Edits inside the modal change filters too, and those must wait for Apply.
  const chipRemoved = useRef(false);
  const [stateSearch, setStateSearch] = useState('');
  // ?view=matches shows what the matcher found instead of the search table.
  const [searchParams, setSearchParams] = useSearchParams();
  const view: 'search' | 'matches' = searchParams.get('view') === 'matches' ? 'matches' : 'search';
  const [matchCount, setMatchCount] = useState<number | null>(null);
  const [savingWatch, setSavingWatch] = useState(false);
  
  const [pagination, setPagination] = useState({
    page: 1, limit: savedView.limit, total: 0, pages: 1,
  });

  const isFreePlan = user?.plan === 'FREE';

  // ---- Saved views ----
  const [views, setViews] = useState<SavedViewRow[]>([]);
  const [watchedViewId, setWatchedViewId] = useState<string | null>(null);
  const [watchBehind, setWatchBehind] = useState(false);
  const [activeViewId, setActiveViewId] = useState<string | null>(searchParams.get('set') || savedView.activeViewId || null);
  const [viewModal, setViewModal] = useState<{ mode: 'create' | 'edit'; view?: SavedViewRow } | null>(null);
  const [viewName, setViewName] = useState('');
  const [viewColor, setViewColor] = useState('slate');
  const [viewDefault, setViewDefault] = useState(false);
  const [viewWatch, setViewWatch] = useState(false);
  const [savingView, setSavingView] = useState(false);
  const [viewNudgeSeen, setViewNudgeSeen] = useState(() => { try { return localStorage.getItem('clevor.views.nudge') === '1'; } catch { return true; } });
  const pendingSet = useRef<string | null>(searchParams.get('set'));

  // The Dashboard's recommended-search link arrives as ?naicsCode=&agency=;
  // nothing read those before. Read once into an unsaved draft, then strip.
  useEffect(() => {
    const naics = searchParams.get('naicsCode');
    const agency = searchParams.get('agency');
    if (!naics && !agency) return;
    chipRemoved.current = true;
    setFilters((f) => ({ ...f, naicsCode: naics ? [naics] : f.naicsCode, agency: agency ? [agency] : f.agency }));
    setActiveViewId(null);
    const params = new URLSearchParams(searchParams);
    params.delete('naicsCode'); params.delete('agency');
    setSearchParams(params, { replace: true });
  // Once, on mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentBody = useCallback((): SavedViewBody => ({
    v: 1,
    filters: { ptype: filters.ptype, setAside: filters.setAside, agency: filters.agency, naicsCode: filters.naicsCode, psc: filters.psc, state: filters.state, includeExpired: filters.includeExpired, includeUnspecifiedState: filters.includeUnspecifiedState },
    searchQuery: searchQuery.trim(),
    sort,
  }), [filters, searchQuery, sort]);

  const activeView = views.find((v) => v.id === activeViewId) || null;
  const viewDirty = !!(activeView?.view && !sameBody(activeView.view, currentBody()));

  const applyView = useCallback((v: SavedViewRow) => {
    if (!v.view) return;
    chipRemoved.current = true; // the [filters] effect refetches page 1
    setFilters(bodyToFilters(v.view));
    setSearchQuery(v.view.searchQuery);
    setSort(v.view.sort);
    setActiveViewId(v.id);
    const params = new URLSearchParams(window.location.search);
    params.set('set', v.id);
    setSearchParams(params, { replace: true });
  // setSearchParams is stable enough for this; the filters setter triggers the fetch.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearView = () => {
    chipRemoved.current = true;
    setFilters(DEFAULT_FILTERS);
    setSearchQuery('');
    setActiveViewId(null);
    const params = new URLSearchParams(window.location.search);
    params.delete('set');
    setSearchParams(params, { replace: true });
  };

  useEffect(() => {
    savedViewsApi.list().then((r) => {
      const d = r.data.data;
      setViews(d.views);
      setWatchedViewId(d.watchedViewId);
      setWatchBehind(d.watchBehind);
      // ?set= from a bookmark, the digest or an agent reply wins over the blob.
      const wanted = pendingSet.current;
      pendingSet.current = null;
      if (wanted) {
        const v = d.views.find((x) => x.id === wanted);
        if (v) applyView(v);
        else { toast('That view no longer exists'); setActiveViewId(null); }
      } else if (!savedView.activeViewId && !localStorage.getItem(STORAGE_KEY)) {
        // A new device: open on the default view, if there is one.
        const def = d.views.find((x) => x.isDefault);
        if (def) applyView(def);
      }
    }).catch(() => { /* the shelf just stays empty */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const suggestName = () => {
    const parts = [
      ...filters.naicsCode.slice(0, 2),
      ...filters.psc.slice(0, 2),
      ...filters.state.slice(0, 2),
      ...filters.agency.slice(0, 1).map((a) => agencyLabel(a)),
      ...filters.setAside.slice(0, 1).map((c) => SET_ASIDE_TYPES.find((sa) => sa.code === c)?.label ?? c),
      ...(searchQuery.trim() ? [`“${searchQuery.trim()}”`] : []),
    ];
    return parts.join(' · ').slice(0, 40) || 'My view';
  };

  const openCreateView = () => {
    setViewName(suggestName());
    setViewColor(VIEW_COLORS[(views.length + 1) % VIEW_COLORS.length].key);
    setViewDefault(views.length === 0);
    setViewWatch(false);
    setViewModal({ mode: 'create' });
    try { localStorage.setItem('clevor.views.nudge', '1'); } catch { /* ignore */ }
    setViewNudgeSeen(true);
  };
  const openEditView = (v: SavedViewRow) => {
    setViewName(v.name); setViewColor(v.color); setViewDefault(v.isDefault); setViewWatch(false);
    setViewModal({ mode: 'edit', view: v });
  };

  const watchView = async (id: string) => {
    try {
      await savedViewsApi.watch(id);
      setWatchedViewId(id);
      setWatchBehind(false);
      const name = views.find((v) => v.id === id)?.name || 'this view';
      toast.success(`Watching for new matches using “${name}”. They show under New matches and in your morning email.`, { duration: 6000 });
      return true;
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't watch this view");
      return false;
    }
  };

  const saveViewModal = async () => {
    if (!viewModal) return;
    const name = viewName.trim();
    if (!name) { toast.error('Give the view a name'); return; }
    setSavingView(true);
    try {
      if (viewModal.mode === 'create') {
        const r = await savedViewsApi.create({ name, color: viewColor, view: currentBody(), isDefault: viewDefault });
        const created: SavedViewRow = r.data.data;
        setViews((vs) => [...vs.map((v) => (viewDefault ? { ...v, isDefault: false } : v)), created]);
        setActiveViewId(created.id);
        const params = new URLSearchParams(window.location.search); params.set('set', created.id); setSearchParams(params, { replace: true });
        toast.success(`Saved “${created.name}”`);
        if (viewWatch && !isFreePlan) await watchView(created.id);
      } else if (viewModal.view) {
        const r = await savedViewsApi.update(viewModal.view.id, { name, color: viewColor, isDefault: viewDefault });
        const updated: SavedViewRow = r.data.data;
        setViews((vs) => vs.map((v) => (v.id === updated.id ? updated : viewDefault ? { ...v, isDefault: false } : v)));
        toast.success('View updated');
        if (viewWatch && !isFreePlan) await watchView(updated.id);
      }
      setViewModal(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't save the view");
    } finally {
      setSavingView(false);
    }
  };

  const saveViewChanges = async () => {
    if (!activeView) return;
    setSavingView(true);
    try {
      const r = await savedViewsApi.update(activeView.id, { view: currentBody() });
      const updated: SavedViewRow = r.data.data;
      setViews((vs) => vs.map((v) => (v.id === updated.id ? updated : v)));
      if (watchedViewId === updated.id) setWatchBehind(true);
      toast.success('View updated');
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't save the view");
    } finally {
      setSavingView(false);
    }
  };

  const deleteView = async () => {
    if (!viewModal?.view) return;
    const v = viewModal.view;
    const extra = v.id === watchedViewId ? ' Your watch criteria are kept.' : v.isDefault ? ' Opportunities will open on All.' : '';
    if (!window.confirm(`Delete the view “${v.name}”?${extra}`)) return;
    setSavingView(true);
    try {
      await savedViewsApi.remove(v.id);
      setViews((vs) => vs.filter((x) => x.id !== v.id));
      if (watchedViewId === v.id) setWatchedViewId(null);
      if (activeViewId === v.id) { setActiveViewId(null); const params = new URLSearchParams(window.location.search); params.delete('set'); setSearchParams(params, { replace: true }); }
      setViewModal(null);
      toast.success('View deleted');
    } catch (e: any) {
      toast.error(e?.response?.data?.error?.message || "Couldn't delete the view");
    } finally {
      setSavingView(false);
    }
  };

  const buildParams = useCallback((page: number, limit: number) => {
    const params: any = { page, limit };
    if (searchQuery) params.q = searchQuery;
    // Pipe-separated, not comma: SAM agency names contain commas
    // ("VETERANS AFFAIRS, DEPARTMENT OF").
    if (filters.agency.length) params.agency = filters.agency.join('|');
    // Multi-select filters travel comma-separated; the API reads any of them.
    if (filters.setAside.length) params.setAside = filters.setAside.join(',');
    if (filters.naicsCode.length) params.naicsCode = filters.naicsCode.join(',');
    if (filters.psc.length) params.psc = filters.psc.join(',');
    // Always send ptype: an empty value means every notice type.
    params.ptype = filters.ptype.join(',');
    if (filters.includeExpired) params.includeExpired = 'true';
    if (sort !== DEFAULT_SORT) params.sort = sort;
    if (filters.state.length) {
      params.state = filters.state.join(',');
      if (!filters.includeUnspecifiedState) params.includeUnspecifiedState = 'false';
    }
    return params;
  }, [searchQuery, filters, sort]);

  const fetchOpportunities = useCallback(async (page = pagination.page, limit = pagination.limit) => {
    try {
      setLoading(true);
      setLoadError(null);
      const params = buildParams(page, limit);
      const response = await opportunityApi.search(params);
      const opps = response.data.opportunities || [];
      setOpportunities(opps);
      setIsLimited(response.data.isLimited || false);
      setUpgradeMessage(response.data.upgradeMessage || '');

      if (response.data.pagination) {
        setPagination({
          page: response.data.pagination.page,
          limit: response.data.pagination.limit,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages,
        });
      }

      if (opps.length > 0) generateNLPSummaries(opps);
    } catch (error: any) {
      console.error('Failed to fetch opportunities:', error);
      setOpportunities([]);
      // A 403 is a plan gate, not a failure — the upgrade banner explains it.
      // Anything else was previously swallowed, leaving an empty table with no
      // explanation and no way to retry.
      if (error.response?.status !== 403) {
        setLoadError(
          error.response?.status === 429
            ? 'Too many searches in a short window. Give it a minute and try again.'
            : "We couldn't reach the opportunity index just now.",
        );
      }
    } finally {
      setLoading(false);
    }
  // generateNLPSummaries is redefined every render; including it would rebuild this callback constantly and defeat the memo.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildParams, pagination.page, pagination.limit]);

  const generateNLPSummaries = async (opps: Opportunity[]) => {
    setLoadingSummaries(true);
    try {
      // Generate summaries for ALL visible opportunities in batches
      const batchSize = 5;
      for (let i = 0; i < opps.length; i += batchSize) {
        const batch = opps.slice(i, i + batchSize);
        
        await Promise.all(
          batch.map(async (opp) => {
            try {
              // Check if we already have this summary cached
              if (summaries[opp.id]) return;
              
              const response = await opportunityApi.getNLPSummary(opp.id);
              setSummaries(prev => ({ ...prev, [opp.id]: response.data.summary }));
            } catch {
              // Silently fail for individual summaries
            }
          })
        );
        
        // Small delay between batches
        if (i + batchSize < opps.length) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }
      }
    } finally {
      setLoadingSummaries(false);
    }
  };

  // Agency options come from SAM's own department list. Counts are scoped to the
  // notice types currently ticked, so "DEPT OF DEFENSE · 2,969 open" is what the
  // user will actually get, not the all-types total.
  const ptypeKey = filters.ptype.join(',');
  useEffect(() => {
    let cancelled = false;
    opportunityApi
      .getAgencies(ptypeKey)
      .then((r) => { if (!cancelled) { setAgencyOptions(r.data.agencies || []); setAgencyLoadFailed(false); } })
      .catch(() => { if (!cancelled) setAgencyLoadFailed(true); });
    return () => { cancelled = true; };
  }, [ptypeKey]);

  // PSC options are the codes actually present in open notices of the ticked
  // types, so the picker never offers a code that returns nothing.
  useEffect(() => {
    let cancelled = false;
    opportunityApi
      .getPscCodes(ptypeKey)
      .then((r) => { if (!cancelled) setPscOptions(r.data.psc || []); })
      .catch(() => { /* picker falls back to typed codes */ });
    return () => { cancelled = true; };
  }, [ptypeKey]);

  useEffect(() => {
    fetchOpportunities(1, savedView.limit);
    // Load inferred preferences for behavioral prompts
    userApi.getInferredPreferences().then(res => setInferredPrefs(res.data)).catch(() => {});
    // Seed watch state so the stars reflect reality on first paint
    watchListApi
      .getAll({ limit: 250 })
      .then(res => {
        const items = res.data?.data?.items || res.data?.items || [];
        setWatchedIds(
          new Set(
            (Array.isArray(items) ? items : [])
              .map((i: any) => i.opportunityId || i.opportunity?.id)
              .filter(Boolean),
          ),
        );
      })
      .catch(() => {});
  // Mount-once initial search plus preference load.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery !== '') fetchOpportunities(1, pagination.limit);
    }, 500);
    return () => clearTimeout(timer);
  // Debounced on searchQuery only. Adding pagination.limit would re-fire the search whenever page size changes, which handleLimitChange already does explicitly.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pagination.pages) fetchOpportunities(newPage, pagination.limit);
  };

  useEffect(() => {
    if (!sortTouched.current) return;
    fetchOpportunities(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  useEffect(() => {
    saveView({ filters, searchQuery, sort, limit: pagination.limit, activeViewId });
  }, [filters, searchQuery, sort, pagination.limit, activeViewId]);

  useEffect(() => {
    if (!chipRemoved.current) return;
    chipRemoved.current = false;
    fetchOpportunities(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const removeChip = (update: (f: Filters) => Filters) => {
    chipRemoved.current = true;
    setFilters(update);
  };

  useEffect(() => {
    opportunityApi.getMatches({ limit: 1 }).then(r => setMatchCount(r.data.newCount)).catch(() => {});
  }, []);

  const showView = (next: 'search' | 'matches') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'matches') params.set('view', 'matches'); else params.delete('view');
    setSearchParams(params, { replace: true });
  };

  // The filters already on screen become what the matcher watches for: same
  // pickers, same names, no second place to maintain criteria.
  const watchCriteria = {
    naicsCodes: filters.naicsCode,
    pscCodes: filters.psc,
    states: filters.state,
    agencies: filters.agency,
    setAsides: filters.setAside,
    keywords: searchQuery.trim() ? [searchQuery.trim()] : [],
  };
  const watchHasCriteria = Object.values(watchCriteria).some(list => list.length > 0);

  const handleSaveWatch = async () => {
    if (!watchHasCriteria) return;
    setSavingWatch(true);
    try {
      await pipelineApi.updateConfig(watchCriteria);
      setWatchedViewId(null); // criteria written outside a view detach the bell
      setIsFilterModalOpen(false);
      toast.success('Watching for new matches. They show under "New matches" and in your morning email.', { duration: 6000 });
    } catch {
      toast.error("Couldn't save what to watch for");
    } finally {
      setSavingWatch(false);
    }
  };

  const handleLimitChange = (newLimit: number) => fetchOpportunities(1, newLimit);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchOpportunities(1, pagination.limit);
  };

  const handleFilterApply = () => {
    setIsFilterModalOpen(false);
    fetchOpportunities(1, pagination.limit);
  };

  const handleFilterClear = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery('');
    fetchOpportunities(1, pagination.limit);
  };

  const activeFiltersCount = [
    filters.ptype.length === 1 && filters.ptype[0] === 'Solicitation' ? 0 : 1,
    filters.setAside.length ? 1 : 0,
    filters.agency.length ? 1 : 0, filters.naicsCode.length ? 1 : 0, filters.psc.length ? 1 : 0, filters.includeExpired ? 1 : 0,
    filters.state.length ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Contract Opportunities"
        subtitle="Search and track federal contract opportunities from SAM.gov"
      />

      {/* Behavioral Preference Confirmation — replaces refine banner when data exists */}
      {inferredPrefs && inferredPrefs.interactionCount >= 3 && !showRefineBanner && (
        <PreferenceConfirmation
          inferredPrefs={inferredPrefs}
          onConfirmed={() => { refreshUser(); fetchOpportunities(1, pagination.limit); }}
        />
      )}

      {/* Instant Value Mode — Refine Banner (only shows for new users without behavioral data) */}
      {showRefineBanner && (!inferredPrefs || inferredPrefs.interactionCount < 3) && (
        <div className="bg-accent-sunk border border-brand-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center">
                <SparklesIcon className="h-4 w-4 text-brand-600" />
              </div>
              <div>
                <p className="font-medium text-content">We're showing initial matches</p>
                <p className="text-sm text-content-muted">Refine them in 30 seconds to fit your business.</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowRefineModal(true)}
                className="btn-primary text-sm px-4 py-2"
              >
                Refine My Matches
              </button>
              <button
                onClick={() => setShowRefineBanner(false)}
                className="text-content-subtle hover:text-content-muted"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {isFreePlan && (
        <div className="bg-gradient-to-r from-brand-600 to-purple-600 rounded-lg px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <SparklesIcon className="h-5 w-5 text-white flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-white">
                You're on the Free plan — seeing limited results
              </p>
              <p className="text-xs text-brand-100 mt-0.5">
                Unlock unlimited search, AI proposals, document analysis, and more with Pro.
              </p>
            </div>
          </div>
          <Link
            to="/pricing"
            className="ml-4 inline-flex items-center px-4 py-2 bg-white text-brand-700 text-sm font-semibold rounded-md hover:bg-brand-50 transition-colors whitespace-nowrap shadow-sm"
          >
            Upgrade to Pro
          </Link>
        </div>
      )}

      {/* Refine Modal */}
      <Modal
        open={showRefineModal}
        onClose={() => setShowRefineModal(false)}
        title="Refine Your Matches"
        description="Tell us a little about your business and we'll show you opportunities that fit."
        size="sm"
        footer={
          <button
            onClick={handleRefineSubmit}
            disabled={refineSubmitting}
            className="w-full btn-primary"
          >
            {refineSubmitting ? 'Refining…' : 'Refine My Matches'}
          </button>
        }
      >
        <div className="space-y-4">
          <NaicsCodePicker
            label="What industry are you in?"
            helpText="Pick one or more NAICS codes — search or browse by category."
            value={refineNaics}
            onChange={setRefineNaics}
          />

          <div>
            <label htmlFor="refine-set-aside" className="label mb-1">Set-Aside Type</label>
            <select
              id="refine-set-aside"
              value={refineSetAside}
              onChange={e => setRefineSetAside(e.target.value)}
              className="input"
            >
              <option value="">Any</option>
              {SET_ASIDE_TYPES.map(s => (
                <option key={s.code} value={s.code}>{s.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="refine-keywords" className="label mb-1">
              Keywords <span className="text-content-subtle font-normal">(optional)</span>
            </label>
            <input
              id="refine-keywords"
              type="text"
              placeholder="e.g. IT support, cybersecurity, cloud migration"
              value={refineKeywords}
              onChange={e => setRefineKeywords(e.target.value)}
              className="input"
            />
          </div>
        </div>
      </Modal>

      <div role="tablist" aria-label="Opportunities view" className="flex gap-1 border-b border-border">
        {([['search', 'Search'], ['matches', 'New matches']] as const).map(([key, label]) => (
          <button
            key={key}
            role="tab"
            aria-selected={view === key}
            onClick={() => showView(key)}
            className={`-mb-px px-4 py-2 text-sm border-b-2 ${view === key ? 'border-accent text-content font-medium' : 'border-transparent text-content-muted hover:text-content'}`}
          >
            {label}
            {key === 'matches' && matchCount !== null && matchCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-accent text-white text-xs">{matchCount > 99 ? '99+' : matchCount}</span>
            )}
          </button>
        ))}
      </div>

      {view === 'matches' ? (
        <MatchesPanel onCountChange={setMatchCount} onEditCriteria={() => setIsFilterModalOpen(true)} isFreePlan={isFreePlan} />
      ) : (<>
      <div className="bg-surface p-4 rounded-lg shadow-sm border border-border">
        <form onSubmit={handleSearch} className="flex flex-col lg:flex-row gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-content-subtle" />
            <input type="text" placeholder="Search by keyword, agency, NAICS code..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-border-strong rounded-lg focus:ring-2 focus:ring-brand-500" />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setIsFilterModalOpen(true)} className="flex items-center gap-2 px-4 py-2 border border-border-strong rounded-lg hover:bg-surface-sunk text-content">
              <AdjustmentsHorizontalIcon className="h-5 w-5" /> Filters
              {activeFiltersCount > 0 && <span className="bg-brand-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">{activeFiltersCount}</span>}
            </button>
            <select value={pagination.limit} onChange={(e) => handleLimitChange(Number(e.target.value))} className="px-3 py-2 pr-8 border border-border-strong rounded-lg appearance-none bg-white" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236B7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em' }}>
              <option value={25}>25 / page</option>
              <option value={50}>50 / page</option>
              <option value={100}>100 / page</option>
              <option value={250}>250 / page</option>
            </select>
            <button type="submit" className="btn-primary flex items-center gap-2"><MagnifyingGlassIcon className="h-5 w-5" /> Search</button>
          </div>
        </form>
        
        {views.length > 0 && (
          <div role="group" aria-label="Saved views" className="mt-3 flex flex-wrap items-center gap-2">
            <button type="button" onClick={clearView} aria-pressed={!activeViewId} className={`inline-flex min-h-[32px] items-center gap-2 rounded-full border px-3 text-sm font-medium ${!activeViewId ? 'border-accent bg-accent text-white' : 'border-border-strong bg-surface text-content hover:bg-surface-sunk'}`}>All</button>
            {views.map((v) => {
              const on = v.id === activeViewId;
              return (
                <span key={v.id} className={`inline-flex min-h-[32px] items-center rounded-full border text-sm font-medium ${on ? 'border-accent bg-accent text-white' : 'border-border-strong bg-surface text-content hover:bg-surface-sunk'}`}>
                  <button type="button" onClick={() => applyView(v)} aria-pressed={on} className="inline-flex items-center gap-2 pl-2.5 pr-2 min-h-[32px]" title={v.isDefault ? `${v.name} · opens by default` : v.name}>
                    <span className={`h-2 w-2 rounded-full ${dotClass(v.color)} ${on ? 'ring-2 ring-white/60' : ''}`} aria-hidden="true" />
                    {v.name}
                    {watchedViewId === v.id && <SparklesIcon className={`h-3.5 w-3.5 ${on ? 'text-white' : 'text-accent'}`} aria-label={watchBehind ? 'Watched; the watch is behind this view' : 'Clevor watches this view for you'} />}
                  </button>
                  <button type="button" onClick={() => openEditView(v)} aria-label={`Edit view ${v.name}`} className={`pr-2.5 pl-1 min-h-[32px] ${on ? 'text-white/80 hover:text-white' : 'text-content-subtle hover:text-content'}`}>···</button>
                </span>
              );
            })}
            <span className="ml-auto text-xs text-content-subtle">{views.length} of 20 views</span>
          </div>
        )}

        {(activeFiltersCount > 0 || activeView || searchQuery.trim()) && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-border items-center">
            {activeView ? (
              <>
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-accent">
                  <span className={`h-2 w-2 rounded-full ${dotClass(activeView.color)}`} aria-hidden="true" />{activeView.name}
                  {viewDirty && <span className="font-normal text-content-muted">· edited</span>}
                </span>
                {viewDirty && (
                  <span className="flex items-center gap-2 text-xs">
                    <button type="button" onClick={saveViewChanges} disabled={savingView} className="text-accent underline">Save changes</button>
                    <button type="button" onClick={openCreateView} className="text-accent underline">Save as new</button>
                    <button type="button" onClick={() => applyView(activeView)} className="text-content-muted underline">Reset</button>
                  </span>
                )}
                {watchedViewId === activeView.id && watchBehind && !isFreePlan && (
                  <button type="button" onClick={() => watchView(activeView.id)} className="text-xs text-warning underline">watch is behind this view · update watch</button>
                )}
                <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
              </>
            ) : (
              <span className="text-sm text-content-muted">Active filters:</span>
            )}
            {filters.ptype.length === 0 && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Type: All</span>}
            {filters.ptype.map(code => (
              <span key={`t-${code}`} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">Type: {PROCUREMENT_TYPES.find(t => t.code === code)?.label ?? code}<button onClick={() => removeChip(f => ({ ...f, ptype: f.ptype.filter(c => c !== code) }))} aria-label={`Remove type ${code}`}><XMarkIcon className="h-3 w-3" /></button></span>
            ))}
            {filters.setAside.map(code => (
              <span key={`s-${code}`} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">Set-Aside: {SET_ASIDE_TYPES.find(sa => sa.code === code)?.label ?? code}<button onClick={() => removeChip(f => ({ ...f, setAside: f.setAside.filter(c => c !== code) }))} aria-label={`Remove set-aside ${code}`}><XMarkIcon className="h-3 w-3" /></button></span>
            ))}
            {filters.agency.map(name => (
              <span key={`a-${name}`} className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-900 text-xs rounded-full">Agency: {agencyLabel(name)}<button onClick={() => removeChip(f => ({ ...f, agency: f.agency.filter(a => a !== name) }))} aria-label={`Remove agency ${name}`}><XMarkIcon className="h-3 w-3" /></button></span>
            ))}
            {filters.naicsCode.map(code => (
              <span key={`n-${code}`} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">NAICS: {code}<button onClick={() => removeChip(f => ({ ...f, naicsCode: f.naicsCode.filter(c => c !== code) }))} aria-label={`Remove NAICS ${code}`}><XMarkIcon className="h-3 w-3" /></button></span>
            ))}
            {filters.psc.map(code => (
              <span key={`p-${code}`} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-full">PSC: {code}{pscOptions.find(o => o.code === code)?.label ? ` · ${pscOptions.find(o => o.code === code)!.label}` : ''}<button onClick={() => removeChip(f => ({ ...f, psc: f.psc.filter(c => c !== code) }))} aria-label={`Remove PSC ${code}`}><XMarkIcon className="h-3 w-3" /></button></span>
            ))}
            {filters.state.map(code => (
              <span key={`st-${code}`} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-full">State: {US_STATES.find(st => st.code === code)?.label ?? code}<button onClick={() => removeChip(f => ({ ...f, state: f.state.filter(c => c !== code) }))} aria-label={`Remove state ${code}`}><XMarkIcon className="h-3 w-3" /></button></span>
            ))}
            {filters.state.length > 0 && filters.includeUnspecifiedState && <span className="inline-flex items-center px-2 py-1 bg-teal-50 text-teal-800 text-xs rounded-full">+ no location listed</span>}
            {filters.includeExpired && <span className="inline-flex items-center gap-1 px-2 py-1 bg-warning-sunk text-warning text-xs rounded-full">Including closed<button onClick={() => setFilters(f => ({ ...f, includeExpired: false }))} aria-label="Stop including closed opportunities"><XMarkIcon className="h-3 w-3" /></button></span>}
            <button onClick={activeView ? clearView : handleFilterClear} className="text-xs text-content-muted hover:text-content underline">Clear all</button>
            {!activeView && (activeFiltersCount > 0 || searchQuery.trim()) && (
              <button type="button" onClick={openCreateView} disabled={views.length >= 20} title={views.length >= 20 ? 'You have 20 views; delete one to add another' : undefined} className="text-xs font-medium text-accent underline disabled:opacity-50">
                {views.length === 0 && !viewNudgeSeen ? 'Save these filters as a view so you can come back to them' : 'Save as view'}
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        open={!!viewModal}
        onClose={() => setViewModal(null)}
        title={viewModal?.mode === 'edit' ? 'Edit view' : 'Save view'}
        description={viewModal?.mode === 'edit' ? undefined : 'Come back to these filters with one tap.'}
        size="sm"
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <div>{viewModal?.mode === 'edit' && <button type="button" onClick={deleteView} disabled={savingView} className="text-sm text-danger hover:underline">Delete view</button>}</div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setViewModal(null)} className="btn-secondary">Cancel</button>
              <button type="button" onClick={saveViewModal} disabled={savingView} className="btn-primary">{savingView ? 'Saving…' : viewModal?.mode === 'edit' ? 'Save' : 'Save view'}</button>
            </div>
          </div>
        }
      >
        <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); saveViewModal(); }}>
          <div>
            <label htmlFor="view-name" className="label mb-1">Name</label>
            <input id="view-name" autoFocus maxLength={40} value={viewName} onChange={(e) => setViewName(e.target.value)} className="input" />
          </div>
          <div>
            <span className="label mb-1 block">Color</span>
            <div className="flex gap-2" role="radiogroup" aria-label="View color">
              {VIEW_COLORS.map((c) => (
                <button key={c.key} type="button" role="radio" aria-checked={viewColor === c.key} aria-label={c.key} onClick={() => setViewColor(c.key)} className={`h-6 w-6 rounded-full ${c.dot} ${viewColor === c.key ? 'ring-2 ring-offset-2 ring-content' : ''}`} />
              ))}
            </div>
          </div>
          {viewModal?.mode === 'create' && (
            <div>
              <span className="label mb-1 block">Filters in this view</span>
              <div className="flex flex-wrap gap-1.5 text-xs">
                {filters.ptype.map((c) => <span key={`t${c}`} className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">{PROCUREMENT_TYPES.find((t) => t.code === c)?.label ?? c}</span>)}
                {filters.naicsCode.map((c) => <span key={`n${c}`} className="px-2 py-0.5 rounded-full bg-green-100 text-green-800">NAICS {c}</span>)}
                {filters.psc.map((c) => <span key={`p${c}`} className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-800">PSC {c}</span>)}
                {filters.state.map((c) => <span key={`s${c}`} className="px-2 py-0.5 rounded-full bg-teal-100 text-teal-800">{US_STATES.find((st) => st.code === c)?.label ?? c}</span>)}
                {filters.agency.map((a) => <span key={`a${a}`} className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900">{agencyLabel(a)}</span>)}
                {filters.setAside.map((c) => <span key={`sa${c}`} className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">{SET_ASIDE_TYPES.find((sa) => sa.code === c)?.label ?? c}</span>)}
                {filters.includeExpired && <span className="px-2 py-0.5 rounded-full bg-warning-sunk text-warning">Including closed</span>}
                {searchQuery.trim() && <span className="px-2 py-0.5 rounded-full bg-surface-sunk text-content">“{searchQuery.trim()}”</span>}
              </div>
              <div className="mt-1.5 text-xs text-content-muted tabular-nums">{pagination.total.toLocaleString()} open right now</div>
            </div>
          )}
          <label className="flex items-start gap-2 text-sm text-content">
            <input type="checkbox" checked={viewDefault} onChange={(e) => setViewDefault(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border-strong text-accent focus:ring-accent" />
            <span>Open Opportunities on this view</span>
          </label>
          {!isFreePlan && (
            <label className="flex items-start gap-2 text-sm text-content">
              <input type="checkbox" checked={viewWatch} onChange={(e) => setViewWatch(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-border-strong text-accent focus:ring-accent" />
              <span>
                Also watch this view for new matches <span className="text-content-muted">(replaces what you watch now)</span>
                <span className="block text-xs text-content-muted">Watching uses NAICS and PSC as prefixes and your search text as one keyword. Notice type and the closed / no-location toggles only affect this page.</span>
              </span>
            </label>
          )}
          <p className="text-xs text-content-subtle">A view is what you look at. Promote one to your watch and it is also what Clevor looks for after every hourly import.</p>
        </form>
      </Modal>

      {upgradeMessage && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4"><p className="text-sm text-yellow-800">{upgradeMessage}</p></div>}

      <div className="flex items-center justify-between">
        <p className="text-sm text-content-muted">Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> - <span className="font-medium">{Math.min(pagination.page * pagination.limit, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results</p>
      </div>

      {/* Learning Feedback — shows after 5+ interactions */}
      {inferredPrefs && inferredPrefs.interactionCount >= 5 && (
        <LearningFeedback
          inferredPrefs={inferredPrefs}
          onConfirmed={() => fetchOpportunities(1, pagination.limit)}
        />
      )}

      {loading ? (
        /* Skeleton rows mirror the real table's column widths, so the layout
           doesn't jump when results land. Was a lone 8px spinner. */
        <div className="card">
          <SkeletonRows rows={8} cols={6} />
        </div>
      ) : loadError ? (
        <div className="card">
          <ErrorState
            title="We couldn't load opportunities"
            description={loadError}
            onRetry={() => fetchOpportunities(pagination.page, pagination.limit)}
          />
        </div>
      ) : opportunities.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={MagnifyingGlassIcon}
            title={isNearMatch ? 'No exact matches yet' : 'No opportunities found'}
            description={
              isNearMatch
                ? "We didn't find exact matches yet — try broadening your filters while we keep monitoring for new ones."
                : 'Try adjusting your search terms or filters.'
            }
            actionLabel={
              filters.naicsCode.length || filters.setAside.length || filters.state.length || filters.agency.length || searchQuery ? 'Show broader results' : undefined
            }
            onAction={() => {
              setFilters(DEFAULT_FILTERS);
              setSearchQuery('');
              setIsNearMatch(true);
            }}
          />
        </div>
      ) : (
        <div className="bg-surface shadow rounded-lg overflow-hidden">
          {/* Phone: one card per opportunity. The sortable headers live in the
              table, so the sort gets its own control here. */}
          <div className="md:hidden">
            <div className="flex items-center justify-between gap-3 px-4 py-2 border-b border-border bg-surface-sunk">
              <span className="text-xs text-content-muted">{pagination.total.toLocaleString()} results</span>
              <label className="flex items-center gap-2 text-xs text-content-muted">
                Sort
                <select
                  value={sort}
                  onChange={(e) => { sortTouched.current = true; setSort(e.target.value as SortKey); }}
                  className="text-xs text-content bg-surface border border-border rounded-md px-2 py-1"
                  aria-label="Sort results"
                >
                  <option value="posted:desc">Newest posted</option>
                  <option value="posted:asc">Oldest posted</option>
                  <option value="due:asc">Soonest due</option>
                  <option value="due:desc">Latest due</option>
                </select>
              </label>
            </div>
            <ul className="divide-y divide-border">
              {opportunities.map((opp) => {
                const due = new Date(opp.responseDeadline);
                const dueClass = due < new Date() ? 'text-red-600' : due.getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000 ? 'text-orange-600' : 'text-green-600';
                return (
                  <li key={opp.id} className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <Link to={`/opportunities/${opp.id}`} className="text-sm font-medium text-brand-600 line-clamp-2">{opp.title}</Link>
                        <p className="text-xs text-content-muted mt-1 truncate">
                          {[cardAgency(opp), opp.naicsCode, opp.setAside].filter(Boolean).join(' · ')}
                        </p>
                        <p className="text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          {sourceInfo(opp.source) && <span className="inline-flex items-center rounded-full bg-surface-sunk px-1.5 py-0.5 text-[11px] font-medium text-content-muted ring-1 ring-inset ring-border" title={`${sourceInfo(opp.source)!.state} ${sourceInfo(opp.source)!.system}`}>{sourceInfo(opp.source)!.state}</span>}
                          <span className={`font-medium ${dueClass}`}>{timeFromNow(opp.responseDeadline)}</span>
                          <span className="text-content-subtle">posted {timeAgo(opp.postedDate)}</span>
                          {formatLocation(opp.placeOfPerformance) && (
                            <span className="inline-flex items-center gap-0.5 text-content-subtle"><MapPinIcon className="h-3 w-3" aria-hidden="true" />{formatLocation(opp.placeOfPerformance)}</span>
                          )}
                        </p>
                        {summaries[opp.id] && <p className="text-xs text-content-muted line-clamp-2 mt-1">{summaries[opp.id]}</p>}
                      </div>
                      <button
                        onClick={() => handleToggleWatch(opp.id)}
                        disabled={togglingWatch === opp.id}
                        aria-label={watchedIds.has(opp.id) ? `Remove ${opp.title} from watch list` : `Add ${opp.title} to watch list`}
                        aria-pressed={watchedIds.has(opp.id)}
                        className="p-2 -mr-2 rounded text-content-subtle hover:text-amber-500 disabled:opacity-40"
                      >
                        {watchedIds.has(opp.id) ? <StarIconSolid className="h-5 w-5 text-amber-500" /> : <StarIcon className="h-5 w-5" />}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-surface-sunk">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-content-muted uppercase w-72">Opportunity</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-content-muted uppercase w-44">Agency</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-content-muted uppercase w-20">NAICS</th>
                  <SortableHeader label="Posted" column="posted" sort={sort} onSort={(k) => { sortTouched.current = true; setSort(k); }} className="w-24" />
                  <SortableHeader label="Due" column="due" sort={sort} onSort={(k) => { sortTouched.current = true; setSort(k); }} className="w-24" />
                  <th className="px-4 py-3 text-left text-xs font-medium text-content-muted uppercase w-24">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-content-muted uppercase"><div className="flex items-center gap-1"><DocumentTextIcon className="h-4 w-4 text-content-subtle" />Quick Summary</div></th>
                  <th className="relative px-4 py-3 w-16"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody className="bg-surface divide-y divide-border">
                {opportunities.map((opp) => (
                  <tr key={opp.id} className="hover:bg-surface-sunk">
                    <td className="px-4 py-4">
                      <Link to={`/opportunities/${opp.id}`} className="text-sm font-medium text-brand-600 hover:text-brand-900 line-clamp-2" title={opp.title}>{opp.title}</Link>
                      <p className="text-xs text-content-muted mt-1">{opp.solicitationNumber && <span className="mr-2">{opp.solicitationNumber}</span>}{opp.setAside && <span className="text-purple-600 mr-2">{opp.setAside}</span>}{formatLocation(opp.placeOfPerformance) && <span className="inline-flex items-center gap-0.5"><MapPinIcon className="h-3 w-3" aria-hidden="true" />{formatLocation(opp.placeOfPerformance)}</span>}</p>
                    </td>
                    <td className="px-4 py-4"><div className="text-sm text-content truncate max-w-[160px]" title={opp.agency}>{formatAgency(opp.agency, { levels: 1 })}</div>{opp.subAgency && <div className="text-xs text-content-muted truncate max-w-[160px]" title={opp.subAgency}>{opp.subAgency}</div>}</td>
                    <td className="px-4 py-4"><span className="text-sm font-mono text-content bg-surface-sunk px-2 py-1 rounded">{opp.naicsCode || 'N/A'}</span></td>
                    <td className="px-4 py-4"><span className="text-sm text-content">{timeAgo(opp.postedDate)}</span></td>
                    <td className="px-4 py-4"><span className={`text-sm font-medium ${new Date(opp.responseDeadline) < new Date() ? 'text-red-600' : new Date(opp.responseDeadline).getTime() - new Date().getTime() < 7 * 24 * 60 * 60 * 1000 ? 'text-orange-600' : 'text-green-600'}`}>{timeFromNow(opp.responseDeadline)}</span></td>
                    <td className="px-4 py-4"><span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{opp.type || 'Solicitation'}</span>{sourceInfo(opp.source) && <span className="ml-1 inline-flex px-2 py-1 text-xs font-medium rounded-full bg-surface-sunk text-content-muted ring-1 ring-inset ring-border" title={`${sourceInfo(opp.source)!.state} ${sourceInfo(opp.source)!.system}`}>{sourceInfo(opp.source)!.state}</span>}</td>
                    <td className="px-4 py-4">{summaries[opp.id] ? <p className="text-xs text-content-muted line-clamp-3" title={summaries[opp.id]}>{summaries[opp.id]}</p> : loadingSummaries ? <div className="flex items-center gap-2 text-xs text-content-subtle"><div className="animate-spin h-3 w-3 border border-border-strong border-t-blue-500 rounded-full"></div>Analyzing...</div> : <span className="text-xs text-content-subtle">-</span>}</td>
                    <td className="px-4 py-4 text-right">
                      <button
                        onClick={() => handleToggleWatch(opp.id)}
                        disabled={togglingWatch === opp.id}
                        aria-label={watchedIds.has(opp.id) ? `Remove ${opp.title} from watch list` : `Add ${opp.title} to watch list`}
                        aria-pressed={watchedIds.has(opp.id)}
                        title={watchedIds.has(opp.id) ? 'Remove from watch list' : 'Add to watch list'}
                        className="p-1 rounded text-content-subtle hover:text-amber-500 disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500"
                      >
                        {watchedIds.has(opp.id)
                          ? <StarIconSolid className="h-5 w-5 text-amber-500" />
                          : <StarIcon className="h-5 w-5" />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {pagination.pages > 1 && (
            <div className="px-6 py-4 bg-surface border-t border-border">
              <div className="flex items-center justify-between">
                <div className="text-sm text-content-muted whitespace-nowrap">Page {pagination.page} of {pagination.pages}</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handlePageChange(pagination.page - 1)} disabled={pagination.page === 1} className="p-2 rounded-lg border border-border-strong hover:bg-surface-sunk disabled:opacity-50"><ChevronLeftIcon className="h-5 w-5" /></button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) pageNum = i + 1;
                      else if (pagination.page <= 3) pageNum = i + 1;
                      else if (pagination.page >= pagination.pages - 2) pageNum = pagination.pages - 4 + i;
                      else pageNum = pagination.page - 2 + i;
                      return <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`w-10 h-10 rounded-lg text-sm font-medium ${pagination.page === pageNum ? 'bg-brand-600 text-white' : 'border border-border-strong hover:bg-surface-sunk'}`}>{pageNum}</button>;
                    })}
                  </div>
                  <button onClick={() => handlePageChange(pagination.page + 1)} disabled={pagination.page === pagination.pages} className="p-2 rounded-lg border border-border-strong hover:bg-surface-sunk disabled:opacity-50"><ChevronRightIcon className="h-5 w-5" /></button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      </>)}

      <Modal
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        title="Advanced Filters"
        icon={FunnelIcon}
        size="lg"
        footer={
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button onClick={handleFilterClear} className="btn-ghost">Clear All</button>
              <button
                type="button"
                onClick={() => (activeView && !viewDirty ? (setIsFilterModalOpen(false), watchView(activeView.id)) : handleSaveWatch())}
                disabled={isFreePlan || !watchHasCriteria || savingWatch}
                title={isFreePlan ? 'Watching for matches is part of the Pro plan' : !watchHasCriteria ? 'Pick at least one filter to watch for' : 'Save these filters as what to watch for'}
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium bg-accent-sunk text-accent ring-1 ring-accent/30 hover:bg-accent/15 hover:ring-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent transition-colors disabled:opacity-50 disabled:hover:bg-accent-sunk disabled:hover:ring-accent/30"
              >
                <SparklesIcon className="h-4 w-4" aria-hidden="true" /> {savingWatch ? 'Saving…' : activeView && !viewDirty ? (watchedViewId === activeView.id && !watchBehind ? 'Watching this view' : 'Watch this view') : 'Watch for new matches'}
              </button>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setIsFilterModalOpen(false)} className="btn-secondary">Cancel</button>
              <button onClick={handleFilterApply} className="btn-primary">Apply Filters</button>
            </div>
          </div>
        }
      >
        <div className="space-y-5">
          <div>
            <MultiLabel
              id="filter-ptype-label"
              text="Procurement Type"
              count={filters.ptype.length}
              onClear={() => setFilters(f => ({ ...f, ptype: [] }))}
              emptyText="All types"
            />
            <CheckList
              labelId="filter-ptype-label"
              options={PROCUREMENT_TYPES}
              selected={filters.ptype}
              onChange={(next) => setFilters(f => ({ ...f, ptype: next }))}
              twoColumn
            />
            <p className="text-caption text-content-muted mt-1">Pick any combination. None selected shows every type.</p>
          </div>

          <div>
            <MultiLabel
              id="filter-set-aside-label"
              text="Set-Aside Type"
              count={filters.setAside.length}
              onClear={() => setFilters(f => ({ ...f, setAside: [] }))}
              emptyText="Any set-aside"
            />
            <CheckList
              labelId="filter-set-aside-label"
              options={SET_ASIDE_TYPES}
              selected={filters.setAside}
              onChange={(next) => setFilters(f => ({ ...f, setAside: next }))}
              disabled={isFreePlan}
              scroll
            />
            {isFreePlan && (
              <p id="filter-set-aside-gate" className="text-caption text-content-muted mt-1">
                Set-aside filtering requires the Pro plan
              </p>
            )}
          </div>

          <div>
            <MultiLabel
              id="filter-state-label"
              text="Place of Performance"
              count={filters.state.length}
              onClear={() => setFilters(f => ({ ...f, state: [] }))}
              emptyText="Anywhere"
            />
            <input
              type="search"
              aria-label="Search states"
              placeholder="Search states, e.g. virginia, TX"
              value={stateSearch}
              onChange={(e) => setStateSearch(e.target.value)}
              className="input mb-2"
            />
            <CheckList
              labelId="filter-state-label"
              // Chosen states stay on top so a pick never vanishes while searching.
              options={[
                ...US_STATES.filter(st => filters.state.includes(st.code)),
                ...US_STATES.filter(st => {
                  if (filters.state.includes(st.code)) return false;
                  const q = stateSearch.trim().toLowerCase();
                  return !q || st.label.toLowerCase().includes(q) || st.code.toLowerCase() === q;
                }),
              ]}
              selected={filters.state}
              onChange={(next) => setFilters(f => ({ ...f, state: next }))}
              twoColumn
              scroll
            />
            {filters.state.length > 0 && (
              <label className="flex items-start gap-3 mt-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filters.includeUnspecifiedState}
                  onChange={(e) => setFilters(f => ({ ...f, includeUnspecifiedState: e.target.checked }))}
                  className="mt-0.5 h-4 w-4 rounded border-border-strong text-accent focus:ring-accent"
                />
                <span>
                  <span className="block text-label text-content">Include opportunities with no location listed</span>
                  <span className="block text-caption text-content-muted mt-0.5">
                    Most solicitations don't name a state. These are listed after the ones in your
                    chosen states, so you don't miss work you could still do.
                  </span>
                </span>
              </label>
            )}
          </div>

          <div>
            <NaicsCodePicker
              label="NAICS Codes"
              helpText="Pick one or more — results match any of them."
              value={filters.naicsCode}
              onChange={(codes) => setFilters(f => ({ ...f, naicsCode: codes }))}
            />
          </div>

          <div>
            <MultiLabel
              id="filter-psc-label"
              text="Product Service Code (PSC)"
              count={filters.psc.length}
              onClear={() => setFilters(f => ({ ...f, psc: [] }))}
              emptyText="Any"
            />
            <p className="text-caption text-content-muted mb-1">What the buyer is buying, where NAICS is what you do. A code or a group prefix: "R4" is every R4xx. Counts are open notices.</p>
            <input
              type="search"
              aria-label="Search PSC codes"
              placeholder="Search codes or names, e.g. R425, valves, janitorial"
              value={pscSearch}
              onChange={(e) => setPscSearch(e.target.value)}
              onKeyDown={(e) => {
                // A typed code or prefix the list does not show (nothing open under it
                // today) can still be added, so a watch can be set ahead of postings.
                const typed = pscSearch.trim().toUpperCase();
                if (e.key === 'Enter' && /^[A-Z0-9]{1,4}$/.test(typed) && !filters.psc.includes(typed)) {
                  e.preventDefault();
                  setFilters(f => ({ ...f, psc: [...f.psc, typed] }));
                  setPscSearch('');
                }
              }}
              className="input mb-2"
            />
            <CheckList
              labelId="filter-psc-label"
              options={(() => {
                const q = pscSearch.trim().toLowerCase();
                const toOpt = (o: PscOption) => ({ code: o.code, label: `${o.code} · ${o.label} (${o.openCount.toLocaleString()})` });
                const chosen = filters.psc.map(code => pscOptions.find(o => o.code === code) ?? { code, label: code, group: '', openCount: 0 }).map(toOpt);
                const rest = pscOptions
                  .filter(o => !filters.psc.includes(o.code))
                  .filter(o => !q || o.code.toLowerCase().startsWith(q) || o.label.toLowerCase().includes(q) || o.group.toLowerCase().includes(q))
                  .slice(0, q ? 60 : 40)
                  .map(toOpt);
                return [...chosen, ...rest];
              })()}
              selected={filters.psc}
              onChange={(next) => setFilters(f => ({ ...f, psc: next }))}
              scroll
            />
          </div>

          <div>
            <MultiLabel
              id="filter-agency-label"
              text="Agency/Organization"
              count={filters.agency.length}
              onClear={() => setFilters(f => ({ ...f, agency: [] }))}
              emptyText="Any agency"
            />
            <input
              type="search"
              aria-label="Search agencies"
              placeholder="Search agencies and sub-agencies, e.g. navy, veterans"
              value={agencySearch}
              onChange={(e) => setAgencySearch(e.target.value)}
              className="input mb-2"
            />
            {agencyLoadFailed ? (
              <p className="text-caption text-danger">Couldn't load the agency list. Try reopening filters.</p>
            ) : (
              <AgencyTree
                labelId="filter-agency-label"
                options={agencyOptions}
                selected={filters.agency}
                onChange={(next) => setFilters(f => ({ ...f, agency: next }))}
                search={agencySearch}
              />
            )}
            <p className="text-caption text-content-muted mt-1">Names as SAM.gov lists them. Expand a department to pick its sub-agencies.</p>
          </div>

          <label className="flex items-start gap-3 pt-4 border-t border-border cursor-pointer">
            <input
              type="checkbox"
              checked={filters.includeExpired}
              onChange={(e) => setFilters(f => ({ ...f, includeExpired: e.target.checked }))}
              className="mt-0.5 h-4 w-4 rounded border-border-strong text-accent focus:ring-accent"
            />
            <span>
              <span className="block text-label text-content">Include closed opportunities</span>
              <span className="block text-caption text-content-muted mt-0.5">
                Off by default, so you only see work you can still bid on. Turn it on to research
                recompetes or see who won something.
              </span>
            </span>
          </label>
        </div>
      </Modal>
    </div>
  );
};

export default Opportunities;
